import { DatePicker } from "antd";
import locale from "antd/locale/fr_FR";
import classnames from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineKeyboard } from "react-icons/md";
import useStableDatePickerValue from "shared/hooks/useStableDatePickerValue";
import useStrictDatePickerInput from "shared/hooks/useStrictDatePickerInput";
import { NAME_SPACES } from "shared/locales/constants";
import calendarMonthIcon from "../../../Assets/icons/calendar_month.svg";
import Label from "../../Label";

const Simple = ({
  label,
  errors,
  icon,
  touched,
  props,
  direction,
  required,
  optional,
  format,
  onChange,
  onBlur,
  value,
  disabledDate,
  ...rest
}) => {
  const { t } = useTranslation(NAME_SPACES.COMPONENTS);
  const DATE_PICKER = t("DATE_PICKER", { returnObjects: true });

  const [clickCount, setClickCount] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const {
    inputError,
    applyMask,
    handleCalendarChange,
    handleDateChange,
    handleBlur,
    handleKeyDown,
  } = useStrictDatePickerInput({
    format,
    invalidDateMessage: DATE_PICKER.INVALID_DATE,
    disallowedDateMessage: DATE_PICKER.DISALLOWED_DATE,
    disabledDate,
    onChange,
    onBlur,
    value,
  });

  const handleCalendarClick = () => {
    setClickCount((prevCount) => prevCount + 1);
  };

  useEffect(() => {
    if (clickCount >= 3) {
      setShowHint(true);
    }
  }, [clickCount]);

  useEffect(() => {
    const datePickerButtons = document.querySelectorAll(".ant-picker-header");

    if (isCalendarOpen) {
      datePickerButtons.forEach((button) => {
        button.addEventListener("click", handleCalendarClick);
      });
    } else {
      datePickerButtons.forEach((button) => {
        button.removeEventListener("click", handleCalendarClick);
      });
    }

    return () => {
      datePickerButtons.forEach((button) => {
        button.removeEventListener("click", handleCalendarClick);
      });
    };
  }, [isCalendarOpen]);

  const handleOpenChange = (open) => {
    setIsCalendarOpen(open);
    if (!open) {
      setClickCount(0);
      setShowHint(false);
    }
    if (typeof rest.onOpenChange === "function") {
      rest.onOpenChange(open);
    }
  };

  const hasValue = value !== null && value !== undefined;
  const shouldHideIcon =
    hasValue && isHovered && !rest.disabled && !rest.allowClear;
  const stableValue = useStableDatePickerValue(value);
  const displayError = inputError || errors;
  const showError = (touched || !!inputError) && displayError;

  return (
    <div
      className={classnames(
        "acoris__datepicker",
        { "acoris__datepicker--error": showError },
        { "acoris__datepicker--valid": touched && !displayError },
        direction,
      )}
    >
      <Label
        label={label}
        icon={icon}
        required={required}
        optional={optional}
      />
      <div
        className="acoris__datepicker--field"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <DatePicker
          suffixIcon={
            shouldHideIcon ? null : (
              <img
                src={calendarMonthIcon}
                alt=""
                className="acoris__datepicker-calendar-icon"
              />
            )
          }
          locale={locale}
          {...props}
          className={classnames({ "disabled--input": rest.disabled })}
          {...rest}
          format={format}
          disabledDate={disabledDate}
          value={stableValue}
          onChange={handleDateChange}
          onCalendarChange={handleCalendarChange}
          onBlur={handleBlur}
          onKeyDown={(event) => {
            applyMask(event);
            handleKeyDown(event);
            if (rest.onKeyDown) rest.onKeyDown(event);
          }}
          onKeyUp={applyMask}
          onOpenChange={handleOpenChange}
          renderExtraFooter={() =>
            showHint && (
              <p className="datePicker-hint-text">
                <MdOutlineKeyboard size={24} />
                <p>{DATE_PICKER.HINT}</p>
              </p>
            )
          }
        />
        {showError && <p className="input--errors">{displayError}</p>}
      </div>
    </div>
  );
};

export default Simple;
